import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useParams, useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { LinearProgress } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { studentApiSlice } from '../../../features/student/studentSlice'
import { groupApiSlice } from '../../../features/group/groupSlice'
import GroupStudentsTable from './components/GroupStudentsTable'
import AddGroupStudentDialog from './components/AddGroupStudentDialog'
import Sentinel from '../../../components/infiniteScroll/Sentinel'
import { usePaginatedList } from '../../../app/hooks/usePaginatedList'
import { ui } from '../../../utils/uiTokens'

interface Props {
    queryParams: any
}

const GroupStudents: React.FC<Props> = ({ queryParams = null }) => {
    const { formatMessage } = useIntl()
    const { groupId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams('')
    const [studentsInfo, setStudentsInfo] = useState([])
    const [fetchGroupStudents] = groupApiSlice.useLazyFetchAllGroupStudentsQuery()

    const {
        rows,
        isLoading: isFetchingGroupStudents,
        loadedPages,
        sentinelRef,
    } = usePaginatedList(
        (page, size) =>
            fetchGroupStudents({
                groupId: Number(groupId),
                params: `page=${page}&size=${size}`,
            }).unwrap(),
        [groupId],
    )

    const [getStudentsInfoByIds, { isLoading: isLoadingStudentsInfo }] =
        studentApiSlice.useGetStudentsInfoByIdsMutation()

    const getStudentsInfo = async () => {
        try {
            const result = await getStudentsInfoByIds(
                rows.map((student) => student.userId),
            ).unwrap()
            setStudentsInfo(result)
        } catch (error) {}
    }

    useEffect(() => {
        if (rows.length) {
            getStudentsInfo()
        }
    }, [rows])

    useEffect(() => {
        setSearchParams({
            course: queryParams.get('course'),
            groupName: queryParams.get('groupName'),
        })
    }, [])

    return (
        <Content>
            <Header>
                <Title>
                    <H3>
                        {searchParams.get('groupName')}{' '}
                        {formatMessage({ id: 'students' })}
                    </H3>
                </Title>

                <AddGroupStudentDialog />
            </Header>

            {isFetchingGroupStudents && loadedPages === 0 ? (
                <LinearProgress />
            ) : (
                <>
                    <GroupStudentsTable
                        studentsData={rows}
                        studentsInfo={studentsInfo}
                        isFetchingGroupStudents={isFetchingGroupStudents}
                        isLoadingStudentsInfo={isLoadingStudentsInfo}
                    />

                    <Sentinel
                        ref={sentinelRef}
                        isLoading={isFetchingGroupStudents}
                    />
                </>
            )}
        </Content>
    )
}

export default GroupStudents

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`
